/**
 * Transfer and redemption requests started from someone else's account view. The shareholder
 * wizards (index.js) file a request for the account being viewed, never for the viewer:
 *
 * - An observer (the viewed shareholder is in the observer's own Users.clients[client].alias) goes
 *   through the wizard the shareholder would. user/submit-transfer-request.py and
 *   user/submit-redemption.py (NewServerless) verify the observer relationship and use the
 *   shareholder path.
 * - An admin previewing the account submits on the shareholder's behalf through the same admin
 *   path as /admin/requests' Create Request (admin-create-request.js): no bank-digits confirmation,
 *   and the request is marked initiated_on_behalf by the backend.
 * - A supervisor or executive preview cannot submit: those roles have no authority to file a
 *   request for another shareholder.
 *
 * In every case the backend records the viewer as initiated_by.
 */

/**
 * How a request started from the current account view is submitted.
 *
 * targetUserid: the shareholder to send as target_userid, or null to submit as the viewer's own
 * request. byAdmin: the admin on-behalf path. disabled: submission is not allowed from this view.
 *
 * @param {{ viewedUserid?: string | null; authedUser?: string | null; isAdmin?: boolean; isPreviewSession?: boolean }} params
 * @returns {{ targetUserid: string | null; byAdmin: boolean; disabled: boolean }}
 */
export function onBehalfRequestContext({ viewedUserid, authedUser, isAdmin, isPreviewSession }) {
	const own = { targetUserid: null, byAdmin: false, disabled: false };
	if (!viewedUserid || !authedUser || viewedUserid === 'consolidated.view') return own;
	if (viewedUserid.trim().toLowerCase() === authedUser.trim().toLowerCase()) return own;
	if (isAdmin) return { targetUserid: viewedUserid, byAdmin: true, disabled: false };
	if (isPreviewSession) return { targetUserid: null, byAdmin: false, disabled: true };
	return { targetUserid: viewedUserid, byAdmin: false, disabled: false };
}

/**
 * inputData with target_userid set; inputData unchanged when targetUserid is null.
 *
 * @param {Record<string, any>} inputData
 * @param {string | null} targetUserid
 */
export function withTargetUserid(inputData, targetUserid) {
	return targetUserid ? { ...inputData, target_userid: targetUserid } : inputData;
}

/**
 * fieldsConfig without the bank-digits confirmation on the admin path (the shareholder's own
 * confirmation, which the backend ignores there -- same as admin-create-request.js's loadTypeConfig).
 *
 * @param {Record<string, boolean>} fieldsConfig
 * @param {boolean} byAdmin
 */
export function fieldsConfigFor(fieldsConfig, byAdmin) {
	return byAdmin ? { ...fieldsConfig, bank_confirmation: false } : fieldsConfig;
}
